export const MODULE_ITEM_ATTR = "data-module-item";
export const MODULE_ITEM_PARENT_ATTR = "data-module-item-parent";

// `isSplittableModule` is what usePageSplitting uses to decide whether a module
// type participates in item splitting at all. A type that is not listed here is
// measured and placed as one block.
export const SPLITTABLE_MODULES = new Set<string>(["toc"]);

export function isSplittableModule(type: string): boolean {
  return SPLITTABLE_MODULES.has(type);
}

export interface ModuleSplitItem {
  height: number;
}

export interface ModuleSplitSpec {
  chrome: number;
  gap: number;
  items: ModuleSplitItem[];
  /**
   * Which visual row each item sits in, measured from the DOM. A module whose
   * items flow into columns puts several items in the same row, and those
   * share one row height instead of stacking — without this a 3-row grid that
   * grows sideways would be measured as if it grew downwards. Absent means one
   * item per row, which is what a table is.
   */
  rowOf?: number[];
  parentOf?: number[];
}

export interface ModuleSlice {
  start: number;
  end: number;
}

export const MODULE_SLICE_KEY = "__slice";

export function sliceOf(module: any): ModuleSlice | undefined {
  return module?.[MODULE_SLICE_KEY];
}

export function sliceModule(module: any, start: number, end: number): any {
  return { ...module, [MODULE_SLICE_KEY]: { start, end } };
}

export function unsliceModules(modules: any[]): any[] {
  const out: any[] = [];
  const seen = new Set<string>();
  for (const mod of modules) {
    const slice = sliceOf(mod);
    if (!slice) {
      out.push(mod);
      continue;
    }
    const id = String(mod?.id ?? "");
    if (seen.has(id)) continue;
    seen.add(id);
    const { [MODULE_SLICE_KEY]: _dropped, ...original } = mod;
    out.push(original);
  }
  return out;
}

export function slicesEqual(
  a: ModuleSlice | undefined,
  b: ModuleSlice | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.start === b.start && a.end === b.end;
}

export function contextOf(spec: ModuleSplitSpec, index: number): number[] {
  const chain: number[] = [];
  if (!spec.parentOf) return chain;
  let parent = spec.parentOf[index] ?? -1;
  while (parent >= 0 && parent < index && !chain.includes(parent)) {
    chain.unshift(parent);
    parent = spec.parentOf[parent] ?? -1;
  }
  return chain;
}

export function heightFor(
  spec: ModuleSplitSpec,
  start: number,
  end: number,
): number {
  if (end <= start) return 0;

  // A row is only as tall as the tallest item the slice puts in it, so an item
  // that joins an existing row can add nothing at all.
  const rowHeights = new Map<number, number>();
  for (let i = start; i < end; i++) {
    const item = spec.items[i];
    if (!item) continue;
    const row = spec.rowOf?.[i] ?? i;
    rowHeights.set(row, Math.max(rowHeights.get(row) ?? 0, item.height));
  }

  let height = spec.chrome;
  for (const ancestor of contextOf(spec, start)) {
    height += (spec.items[ancestor]?.height ?? 0) + spec.gap;
  }
  let first = true;
  for (const row of Array.from(rowHeights.keys()).sort((a, b) => a - b)) {
    if (!first) height += spec.gap;
    height += rowHeights.get(row) ?? 0;
    first = false;
  }
  return height;
}
